import type { MouseEvent } from "react";
import { generatePath, useMatch, useParams } from "react-router";
import { useTranslations } from "@acto/web-common/locales";
import {
  BarChart,
  BarChartOutline,
  BookOpenFilled,
  BookOpenOutline,
  ChatBubble,
  CheckSquareRounded,
  CheckSquareRoundedFilled,
  MicrophoneOutline,
} from "@acto/web-common/components";
import ActoRoutes from "@acto/taskpane/core/router/routes.config";
import { ShellSlotHost, useShellDismiss } from "@acto/taskpane/field-sales-2/shell";
import { useOpenCustomerChat } from "@acto/taskpane/field-sales-2/utils/useOpenCustomerChat";
import { useDirectionalNavigate } from "@acto/taskpane/field-sales-2/utils/viewTransitions";
import {
  BottomNavActionPill,
  BottomNavCircleButton,
  BottomNavShell,
  BottomNavTabRow,
  BottomNavTabBar,
  type BottomNavTab,
} from "./components/patterns/BottomNav";

export function CustomerBottomNav() {
  const t = useTranslations("fieldSalesTwo.bottomNav");
  const { id } = useParams<{ id: string }>();
  const openChat = useOpenCustomerChat(id);
  const navigate = useDirectionalNavigate();
  const { requestDismiss } = useShellDismiss();

  // Ad-hoc recap: jump into the Besuchsberichte screen, which opens the meeting-less creation
  // flow for this customer (`autoStartAdHocRecap`).
  const startAdHocRecap = () => {
    if (!id) return;
    navigate(generatePath(ActoRoutes.CUSTOMER_RECAPS, { id }), {
      direction: "push",
      state: { autoStartAdHocRecap: true },
    });
  };

  const overviewPath = id ? generatePath(ActoRoutes.CUSTOMER_DETAILS, { id }) : undefined;
  const preparePath = id ? generatePath(ActoRoutes.MEETING_PREP, { id }) : undefined;
  const todosPath = id ? generatePath(ActoRoutes.CUSTOMER_TODOS, { id }) : undefined;
  const isOverview = Boolean(useMatch({ path: ActoRoutes.CUSTOMER_DETAILS, end: true }));
  const isPrepare = Boolean(useMatch(ActoRoutes.MEETING_PREP));
  const isTodos = Boolean(useMatch(ActoRoutes.CUSTOMER_TODOS));

  // Re-tapping the active tab would navigate to the route it is already on, replaying the page enter
  // animation. Dismissing is the whole gesture, so it cancels the navigation.
  const dismissOnReselect = (isActive: boolean) =>
    isActive
      ? (event: MouseEvent<HTMLElement>) => {
          if (requestDismiss()) event.preventDefault();
        }
      : undefined;

  const tabs: BottomNavTab[] = [
    {
      key: "overview",
      label: t("overview"),
      icon: <BarChartOutline width={26} height={26} aria-hidden="true" />,
      activeIcon: <BarChart width={26} height={26} aria-hidden="true" />,
      to: overviewPath,
      isActive: isOverview,
      onClick: dismissOnReselect(isOverview),
    },
    {
      key: "prepare",
      label: t("prepare"),
      icon: <BookOpenOutline width={26} height={26} aria-hidden="true" />,
      activeIcon: <BookOpenFilled width={26} height={26} aria-hidden="true" />,
      to: preparePath,
      isActive: isPrepare,
      onClick: dismissOnReselect(isPrepare),
    },
    {
      key: "tasks",
      label: t("tasks"),
      icon: <CheckSquareRounded width={26} height={26} aria-hidden="true" />,
      activeIcon: <CheckSquareRoundedFilled width={26} height={26} aria-hidden="true" />,
      to: todosPath,
      isActive: isTodos,
      onClick: dismissOnReselect(isTodos),
    },
  ];

  if (!tabs.some((tab) => tab.isActive)) return null;

  return (
    <BottomNavShell>
      <ShellSlotHost slot="bottomNavAction" hiddenDuringOverlay>
        <BottomNavActionPill onClick={startAdHocRecap} disabled={!id}>
          <MicrophoneOutline width={18} height={20} aria-hidden="true" />
          {t("newFollowUp")}
        </BottomNavActionPill>
      </ShellSlotHost>
      <ShellSlotHost slot="bottomNavTabRow">
        <BottomNavTabRow>
          <BottomNavTabBar tabs={tabs} />
          <BottomNavCircleButton
            icon={<ChatBubble width={24} height={24} aria-hidden="true" />}
            onClick={openChat}
            aria-label={t("openChat")}
          />
        </BottomNavTabRow>
      </ShellSlotHost>
    </BottomNavShell>
  );
}
